import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { Championship, Organizer } from 'modules/stream/domain/interfaces/game-info/gameInfoMapped';
import { FoulsByPeriod, Goals, Match, ShootoutScore, Teams } from 'modules/stream/domain/interfaces/game-info/state';

import { Event } from '../../domain/interfaces/events';
import { createFetchPassedEventsReducer, createResetAllReducer, createSyncDataReducer } from './reducers';
import parseInfoEvent from './utils/parse-info.event';

export type GameInfoSliceState = {
  match?: Match;
  teams?: Teams;
  organizer?: Organizer;
  championship?: Championship;

  goals: Goals;
  fouls: FoulsByPeriod;
  shootout: { goals: ShootoutScore; misses: ShootoutScore };

  isLoading: boolean;
  isLoaded: boolean;

  isSynchronization: boolean;
  isSynchronize: boolean;

  parsedEventsId: number[];
  error?: string;
};

const initialState: GameInfoSliceState = {
  isLoaded: false,
  isLoading: false,
  isSynchronization: false,
  isSynchronize: false,
  goals: { first: 0, second: 0 },
  fouls: [],
  shootout: { goals: { first: 0, second: 0 }, misses: { first: 0, second: 0 } },
  parsedEventsId: [],
  error: undefined,
};

export const gameInfoSlice = createSlice({
  name: 'gameInfo',
  initialState,
  reducers: {
    setGoals: (state, action: PayloadAction<{ first?: number; second?: number }>) => {
      action.payload.first !== undefined && (state.goals.first = action.payload.first);
      action.payload.second !== undefined && (state.goals.second = action.payload.second);
    },
    setFouls: (state, action: PayloadAction<FoulsByPeriod>) => {
      state.fouls = action.payload;
    },
    setFoulsOnPeriod: (state, action: PayloadAction<{ first?: number; second?: number; period: number }>) => {
      if (!action.payload.period) return;
      state.fouls[action.payload.period] = {
        first: Math.max(0, action.payload.first ?? state.fouls[action.payload.period]?.first ?? 0),
        second: Math.max(0, action.payload.second ?? state.fouls[action.payload.period]?.second ?? 0),
      };
    },
    parseEvent: (state, action: PayloadAction<{ events: Event[] }>) => {
      for (const event of action.payload.events) {
        if (_.find(state.parsedEventsId, (id) => id === event.id)) continue;
        state.parsedEventsId.push(event.id);
        switch (event.type) {
          case 'GOAL':
          case 'PASS_AND_GOAL':
          case 'PENALTY_GOAL': {
            const { teamPrefix, team, player } = parseInfoEvent(event, state.teams);
            if (teamPrefix) {
              state.goals[teamPrefix] += 1;
            }
            if (team && player) {
              team.members = team.members?.map((member) => {
                if (member.id === player.id) {
                  member.qtyGoalsMatch += 1;
                }
                return member;
              });
            }
            break;
          }
          case 'AFTER_MATCH_PENALTY_GOAL': {
            const { teamPrefix, team, player } = parseInfoEvent(event, state.teams);
            if (teamPrefix) {
              state.shootout.goals[teamPrefix] += 1;
            }
            if (team && player) {
              team.members = team.members?.map((member) => {
                if (member.id === player.id) {
                  member.qtyShootoutGoalsMatch += 1;
                }
                return member;
              });
            }
            break;
          }
          case 'AFTER_MATCH_PENALTY_MISSED': {
            const { teamPrefix, team, player } = parseInfoEvent(event, state.teams);
            if (teamPrefix) {
              state.shootout.misses[teamPrefix] += 1;
            }
            if (team && player) {
              team.members = team.members?.map((member) => {
                if (member.id === player.id) {
                  member.qtyShootoutMissesMatch += 1;
                }
                return member;
              });
            }
            break;
          }
          case 'OWN_GOAL': {
            const { teamPrefix } = parseInfoEvent(event, state.teams, { teamReverse: true });
            if (teamPrefix) {
              state.goals[teamPrefix] += 1;
            }
            break;
          }
          case 'FOUL': {
            const period = event.period;
            if (!period) return;

            const isFirst = event.teamId === state.teams?.first.id;
            const teamPrefix = isFirst ? 'first' : 'second';
            const team = state.teams?.[teamPrefix];
            if (team) {
              if (!state.fouls[period]) {
                state.fouls[period] = { first: 0, second: 0 };
              }
              state.fouls[period][teamPrefix] += 1;
            }
            break;
          }
          case 'RED_CARD': {
            const { player } = parseInfoEvent(event, state.teams);
            if (!player) break;
            player.qtyRedCardsMatch += 1;
            break;
          }
          case 'YELLOW_CARD': {
            const { player } = parseInfoEvent(event, state.teams);
            if (!player) break;
            player.qtyYellowCardsMatch += 1;
            break;
          }
        }
      }
    },
    setPassedEventsId: (state, action: PayloadAction<number[]>) => {
      state.parsedEventsId = action.payload;
    },
  },
  extraReducers: (builder) => {
    createSyncDataReducer(builder);
    createResetAllReducer(builder);
    createFetchPassedEventsReducer(builder);
  },
});

export const { setGoals, setFouls, parseEvent, setPassedEventsId, setFoulsOnPeriod } = gameInfoSlice.actions;
